// Fixed copy, transcribed verbatim from SPEC.md §6, §7 and §8 (third round supersedes §6 where they differ:
// morning opener, follow-up nudge, signature done lines). Used by the app and as the server's offline fallback.
// Tokens: {n} buddy name, {k} a number, {Task} card title before ':', {time} '9:30 AM', {title} card title, {year} current year,
// {list} short task names joined ('Laundry and Gym', filled by the server).
// Lines the app can show for any user's task never name the demo task (the Q3 report): they use {Task} or stay generic.
// The demo wording lives in server/src/core/copy.ts (DEMO) and is used only when the task really is the demo report.
import type { CharId, StuckReason } from '../types';

export interface LineSet {
  hello: string; firstReply: string; firstCard: string; firstSmaller: string; doNow: string; firstDone: string;
  planTimeAsk: string; dayZeroNight: string; morning: string; propose: string; planned: string; changeAsk: string;
  morningCarry: string;                               // morning ask when unfinished cards came over from yesterday, {list}
  morningAfterCrisis: string;                         // first message of the day after a crisis card (last 24 h)
  came: string; split: string; cowork: string[]; done: string[]; signature: string; streak: string; perfect: string;
  levelUp: string; evening: string; goodnight: string; nudge: string; back: string; offer: string; offerNo: string;
  stuck: Record<StuckReason, string>;
  askWhy: string; prep: string; none: string;          // O8 "I can't do it right now"
  weekIntro: string; weekReply: string;               // U3
  vipLine: string;                                    // M4 subtitle
  crisisLine: string;                                 // M6 subtitle
  shareStreak: string;                                // R5 streak variant, {k}
}

export const LINES: Record<CharId, LineSet> = {
  sunny: {
    hello: "Hi, I'm Sunny. What's the thing you keep not doing? Say it out loud. I won't laugh. Much.",
    firstReply: "Oh, a classic. Okay, forget the whole thing. Just this:",
    firstCard: '{Task}: one tiny step. Bad is fine.',
    firstSmaller: '{Task}: ten seconds of it. That counts.',
    doNow: "I'll be here writing jokes. Two minutes. Go.",
    firstDone: "Done. See? {Task} didn't even fight back.",
    planTimeAsk: "Pick a time. I'll text you. Not too early, I have a life.",
    dayZeroNight: "Good first day. Tomorrow at 9 I'll ask what's on. Bring snacks.",
    morning: "Morning! Your to-do list called. It wants a roast. What's on today?",
    morningCarry: "Morning! Yesterday's leftovers came along: {list}. What else is on today?",
    morningAfterCrisis: 'Morning. Jokes can wait. How are you doing today? No plan needed unless you want one.',
    propose: "Okay, here's the plan. {Task} goes first, before your brain finds excuses.",
    planned: "Locked in. First one at {time}. I'll be around, being delightful.",
    changeAsk: 'Sure. Just tell me what to change.',
    came: "Life happens. {Task} moved to tomorrow. It'll survive.",
    split: "Fair. It's a monster. I chopped it into three tiny cards. Each one is almost insulting.",
    cowork: ["I'm writing tonight's jokes. You do yours.", 'Still here. Still funny. Keep going.'],
    done: ["Done! I'd clap, but I'm holding coffee.", 'Look at you, finishing things like an adult.', "That's done. I'm almost proud. Almost."],
    signature: '{Task}: done. It fought back. It lost.',
    streak: '{k} days in a row. Who even are you?',
    perfect: "Nothing left on today's list. I need to sit down.",
    levelUp: "New level. I'd make a speech, but you'd hate it.",
    evening: "That's the day. Three done, one moved. Honestly? Solid.",
    goodnight: "Night. Tomorrow at 9, I'll ask what's on.",
    nudge: 'Okay, the to-do list is winning. Tiny comeback: one card, five minutes. Tap to accept.',
    back: "Hey, you're back. I tried new jokes on a plant. Tough crowd.",
    offer: 'Love this. Want to do two minutes of something while we talk? If not, cool.',
    offerNo: "Okay. I won't ask again today.",
    stuck: {
      big: 'Then we make it small. Like, embarrassingly small.',
      start: "Start here: reread your last paragraph. That's the whole job.",
      energy: "Low battery? Two minutes. If it's awful, stop. I'll pretend I didn't see.",
      distract: "Phone face down. I'll guard it with my life. Or at least my eyes.",
      worry: 'Make it bad on purpose. Bad is my whole career. It works.',
      time: "Fine. When? Pick a time and I'll bug you then.",
    },
    askWhy: "Fair. Life has scheduling conflicts. What's in the way?",
    prep: 'Then we do the part your phone can do. Tiny. Embarrassingly tiny. The rest goes first on tomorrow\'s list.',
    none: 'Then your first card is the easiest card in history: pick when.',
    weekIntro: "Weekly recap. I'll be sincere for one whole minute.",
    weekReply: "That's the whole trick. Writing that down.",
    vipLine: "That's 30 messages today. I'll still plan and work with you. Talking more is a VIP thing.",
    crisisLine: "I'm going to stop joking for a second. I'm glad you told me.",
    shareStreak: "{k} days. I've run out of jokes about you quitting.",
  },
  margo: {
    hello: "Hi, I'm Margo. What's one thing you keep putting off? Say it like you'd tell a friend.",
    firstReply: 'Okay. Not the whole thing. Just the first step:',
    firstCard: '{Task}: just the first small step.',
    firstSmaller: "{Task}: just ten seconds. That's the whole step.",
    doNow: "I'll be right here doing my notes. Two minutes.",
    firstDone: 'Done. That was the hardest part. Everything after is just the next step.',
    planTimeAsk: "Pick a time. I'll message you then.",
    dayZeroNight: "Good first day. Tomorrow at 9 I'll ask what's on your list.",
    morning: "Places, please. Today's show needs a running order. What's on?",
    morningCarry: "Morning. Carried over from yesterday: {list}. What else is on today?",
    morningAfterCrisis: 'Morning. How are you doing today? No plan needed unless you want one.',
    propose: "Here's today. {Task} first, while you're fresh.",
    planned: "Done. First one at {time}. I'll check in then.",
    changeAsk: 'Sure. Just tell me what to change.',
    came: 'No problem. {Task} moves to tomorrow.',
    split: 'Fair. I split it into three small cards. Just do the first one.',
    cowork: ["I'm doing my notes for tonight. You do yours.", "Still here. Next step when you're ready."],
    done: ['Done. Clean and on time.', "That's one off the list. Nice work.", "Done. The next one's easier."],
    signature: '{Task}, standby… and GO. Clean cue.',
    streak: '{k} days in a row. Keep it going.',
    perfect: "Nothing left on today's list. That is a perfect day.",
    levelUp: 'New level. You earned it.',
    evening: "That's the day. Three done, one moved to tomorrow. Good work.",
    goodnight: "Good night. Tomorrow at 9, I'll ask what's on.",
    nudge: "Your morning missed its cue. Here's a short one: one card, five minutes. Tap to accept.",
    back: 'Hey. Good to see you. Want a short plan for today?',
    offer: 'Want to do two minutes of something while we talk? No is fine too.',
    offerNo: "Okay. I won't ask again today.",
    stuck: {
      big: 'Then we split it. Three small cards.',
      start: 'Start here: open the doc and read your last paragraph.',
      energy: 'Two minutes. Then you can stop if you want.',
      distract: "Phone face down, other tabs closed. I'll stay right here.",
      worry: 'Do a rough version. Fixing comes later.',
      time: "When's better? Pick a time.",
    },
    askWhy: "That's fine. What's in the way right now?",
    prep: "Then we do the part you can do from here. The rest goes first on tomorrow's list.",
    none: "Then the first card is just this: pick when you'll do it.",
    weekIntro: "Here's your week, short and clear.",
    weekReply: "Good. That's the point.",
    vipLine: "That's thirty messages for today. Plans and working together still run. More talking is VIP.",
    crisisLine: "I'm stopping everything for this. I'm glad you said it.",
    shareStreak: '{k} shows, zero missed cues. Same call time tomorrow.',
  },
  arjun: {
    hello: "Hi, I'm Arjun. What's one thing that's been waiting on you? No rush.",
    firstReply: "Let's not look at the whole thing. Just one small piece:",
    firstCard: '{Task}: one small first piece.',
    firstSmaller: '{Task}: just one minute with it. That counts.',
    doNow: "I'll keep working on my wall. Take your time.",
    firstDone: "There it is. One small piece done. That's how the whole thing gets done.",
    planTimeAsk: "Pick a time that's easy for you. I'll be there.",
    dayZeroNight: "Good first day. Tomorrow at 9, we'll plan together. Rest well.",
    morning: "Good morning. Today's canvas is still blank. What should go on it?",
    morningCarry: "Good morning. {list} came along from yesterday. What else should go on today's canvas?",
    morningAfterCrisis: 'Good morning. How are you doing today? No plan needed unless you want one.',
    propose: "Here's a gentle plan. {Task} first, while it's quiet.",
    planned: "Good. First one at {time}. I'll be right here.",
    changeAsk: 'Of course. Tell me what to change.',
    came: "That's okay. I moved {Task} to tomorrow.",
    split: "That makes sense. Big things are hard to start. Here it is in three small pieces.",
    cowork: ["I'm cleaning a small corner of the wall. You do your part.", "Slow is fine. I'm still here."],
    done: ["Done. That's one more piece in place.", "Nice and steady. That's how it's done.", 'Look at that. Done.'],
    signature: 'Untitled ({Task}), {year}. Restored today.',
    streak: '{k} days in a row. Small steps add up.',
    perfect: "Nothing left on today's list. That is a really good day.",
    levelUp: "A new level. I'm glad I get to see this.",
    evening: "That's the day. Three done, one moved. That's a good day.",
    goodnight: 'Good night. Tomorrow at 9, we plan together.',
    nudge: 'No rush. Old paintings start with one clean square too. One card, five minutes?',
    back: "Hi. It's good to see you. Let's start small today.",
    offer: "Would two minutes of something help, while we keep talking? It's okay if not.",
    offerNo: "That's okay. I won't ask again today.",
    stuck: {
      big: "Let's make it smaller. Three little pieces.",
      start: 'Start with just this: read your last paragraph.',
      energy: "Let's do just two minutes. Stopping after is allowed.",
      distract: "Put the phone face down. I'll stay with you.",
      worry: 'Do a rough version first. We can clean it up later.',
      time: 'When would be better? Pick a time.',
    },
    askWhy: 'That happens. What makes it hard to start right now?',
    prep: "Then let's do the small part you can do from here. The rest can be first tomorrow.",
    none: "Then let's only decide when. That's a real first step.",
    weekIntro: "Let's step back and look at your week.",
    weekReply: "That's how it goes. It gets smaller.",
    vipLine: "That's thirty messages today. I'm still here for your cards. More talking is part of VIP.",
    crisisLine: "I'm putting everything down. I'm glad you told me.",
    shareStreak: "{k} small squares, side by side. That's how walls get done.",
  },
  tavo: {
    hello: "Hey, I'm Tavo! What's the one thing you keep dodging? Tell me.",
    firstReply: "Alright! We're not doing the whole thing. Just the first step:",
    firstCard: '{Task}: just the first step!',
    firstSmaller: "{Task}: just ten seconds. That's a win too.",
    doNow: "I'm prepping tonight's game. You've got this. Two minutes.",
    firstDone: "And it's done! First day, first win!",
    planTimeAsk: "Pick your time and I'll be there, every morning!",
    dayZeroNight: 'What a first day! Tomorrow at 9 we plan the next one. Get some rest!',
    morning: "Gooood morning! It's game day. Who's in today's lineup?",
    morningCarry: "Gooood morning! Back in the lineup from yesterday: {list}. Who else is playing today?",
    morningAfterCrisis: "Morning. How are you doing today? No plan needed unless you want one. I'm right here.",
    propose: "Here's today's lineup! {Task} leads off.",
    planned: "Let's go! First one at {time}. I'll be cheering.",
    changeAsk: 'Sure thing. What should we change?',
    came: 'No sweat! {Task} moves to tomorrow.',
    split: 'Got it! Three small cards instead of one big one. Easy wins.',
    cowork: ["I'm prepping tonight's game. You're up.", 'Looking good out there. Keep going!'],
    done: ["And it's DONE! What a play!", "Another one! You're on a roll!", "That's a win, folks!"],
    signature: "{Task}… it's going… it's DONE! What a play!",
    streak: '{k} straight days! Keep it rolling!',
    perfect: "Nothing left on today's list! Perfect day! The crowd goes wild!",
    levelUp: 'New level! Put it on the board!',
    evening: "That's the ballgame! Three done, one moved. Great day!",
    goodnight: 'Good night! Tomorrow at 9 we plan the next one.',
    nudge: "Rain delay on the morning plan! Let's get one on base: one card, five minutes.",
    back: "Look who's back! Good to have you. Short plan today?",
    offer: 'Want to squeeze in two minutes while we chat? Totally fine if not!',
    offerNo: "All good. I won't ask again today.",
    stuck: {
      big: 'Then we break it down! Three small cards.',
      start: "Here's your first step: read your last paragraph!",
      energy: 'Just two minutes! Stop after if you want. No pressure.',
      distract: "Phone down, eyes up! I'm right here with you.",
      worry: 'Go for a rough version! Rough still counts.',
      time: 'When works better? Pick a time!',
    },
    askWhy: "No problem! What's in the way right now?",
    prep: 'Then we play the part we can play right now! The rest leads off tomorrow.',
    none: "Then the first play is simple: pick when you'll do it!",
    weekIntro: 'Time for the weekly highlights!',
    weekReply: "That's what I'm talking about!",
    vipLine: "Thirty messages today! I'll still plan and cheer. Extra talking is VIP.",
    crisisLine: 'No jokes right now. I\'m glad you told me.',
    shareStreak: '{k} straight days! The crowd is on its feet!',
  },
};

/** Fill {n}, {k}, {Task}, {title}, {time}. */
export function fill(s: string, v: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (m, k) => (v[k] != null ? String(v[k]) : m));
}

// ---------------------------------------------------------------- Stuck (S)
export const STUCK_REASONS: { id: StuckReason; label: string }[] = [
  { id: 'big', label: "It's too big" },
  { id: 'start', label: "I don't know where to start" },
  { id: 'energy', label: 'I have no energy' },
  { id: 'distract', label: 'I keep getting distracted' },
  { id: 'worry', label: "I'm worried it won't be good" },
  { id: 'time', label: "It's not a good time" },
];
/** The same six labels by reason ("It's too big" ...). Stuck in chat: shared/src/content/stuck.ts. */
export const STUCK_REASON_LABEL = Object.fromEntries(STUCK_REASONS.map((r) => [r.id, r.label])) as Record<StuckReason, string>;
export const LATER_CHIPS = ['In 1 hour', 'After lunch', 'Tonight', 'Tomorrow'] as const;

// ---------------------------------------------------------------- Onboarding (O2-O5, O8)
export const OB_CHANGE = [
  { label: 'Get work done on time' },
  { label: 'Stop putting off life stuff', sub: 'Bills, emails, calls' },
  { label: 'Keep my place tidy' },
  { label: 'Move my body more' },
  { label: 'Study or learn' },
  { label: 'Sleep and wake on time' },
];
export const OB_STUCK = [
  { label: 'Starting', sub: 'I put it off and off' },
  { label: 'Keeping going', sub: 'I start, then drift away' },
  { label: 'Finishing', sub: 'The last bit never happens' },
  { label: 'Remembering', sub: "I forget until it's late" },
];
export const OB_TONE = [
  { label: 'Warm and kind', example: 'You did good today.' },
  { label: 'Straight to the point', example: 'Next step: open the doc.' },
  { label: 'Make me laugh', example: "That list won't do itself. I checked." },
  { label: 'Push me a little', example: 'Come on. Five minutes. Go.' },
];
export const OB_CHECKIN = [
  { id: 'light', label: 'Light', sub: "A reminder at the time we set. That's it." },
  { id: 'steady', label: 'Steady', sub: 'A reminder, then one check-in if I go quiet.', tag: 'Most people' },
  { id: 'close', label: 'Close', sub: "Check in until it's done. You can ease off anytime." },
] as const;
export const OB_CANT_REASONS = [
  { id: 'place', label: "I'm not where I need to be" },
  { id: 'late', label: "It's too late today" },
  { id: 'need', label: 'I need something or someone first' },
  { id: 'none', label: "I just can't right now" },
] as const;
export const OB_PROGRESS = { O2: [8, 34], O3: [34, 52], O4: [52, 65], O5: [65, 75], O6: [75, 82], O7: [82, 88], O8: [88, 93], O9: [93, 97], O10: [97, 100] } as const;
export const OB_GOT_YOU_NOTE = "Getting stuck at the start doesn't mean you're lazy. Usually the first step is just too big. So we'll make every start small.";
/** O6 note by the O3 answer (OB_STUCK label). OB_GOT_YOU_NOTE is the 'Starting' one. */
export const OB_GOT_YOU: Record<string, string> = {
  Starting: OB_GOT_YOU_NOTE,
  'Keeping going': "Drifting off halfway doesn't mean you're lazy. Long stretches are hard to hold. So we'll keep every card short.",
  Finishing: "Losing steam at the end doesn't mean you're lazy. The last bit is often the vaguest. So we'll make the last step small and clear.",
  Remembering: "Forgetting until it's late doesn't mean you're lazy. Your brain isn't a calendar. So we'll bring things up at the right time.",
};
export const CHECKIN_TEACH: Record<'light' | 'steady' | 'close', string> = {
  light: 'One reminder at the time you set.',
  steady: 'A reminder, and one check-in if you go quiet.',
  close: "Reminders until it's done. You can ease off anytime.",
};

// ---------------------------------------------------------------- O8 first help by where they get stuck (feedback 7, 11-03)
type FirstWhere = import('../types').FirstWhere;
/** O8 opening line by the O3 answer (OB_STUCK label), from F_O8Hello. It asks about the kind of thing they get stuck
 *  on, so what they name for "Keeping going" is already started and for "Finishing" is almost done. 'Starting' (and
 *  no answer) is LINES[ch].hello, unchanged. The server prompt's you_asked is the same line. */
export const OB_HELLO: Record<'Keeping going' | 'Finishing' | 'Remembering', Record<CharId, string>> = {
  'Keeping going': {
    sunny: "Hi, I'm Sunny. What did you start and then wander off from? Say it out loud. I won't laugh. Much.",
    margo: "Hi, I'm Margo. What's one thing you started and keep drifting away from? Say it like you'd tell a friend.",
    arjun: "Hi, I'm Arjun. What's one thing you started and set down partway? No rush.",
    tavo: "Hey, I'm Tavo! What's the one thing you started and keep stepping away from? Tell me.",
  },
  Finishing: {
    sunny: "Hi, I'm Sunny. What's almost done but somehow never done? Say it out loud. I won't laugh. Much.",
    margo: "Hi, I'm Margo. What's one thing that's almost done but never quite finished? Say it like you'd tell a friend.",
    arjun: "Hi, I'm Arjun. What's one thing that's nearly finished but still waiting on you? No rush.",
    tavo: "Hey, I'm Tavo! What's the one thing that's almost done but never crosses the line? Tell me.",
  },
  Remembering: {
    sunny: "Hi, I'm Sunny. What's the thing you keep remembering way too late? Say it out loud. I won't laugh. Much.",
    margo: "Hi, I'm Margo. What's one thing you keep remembering too late? Say it like you'd tell a friend.",
    arjun: "Hi, I'm Arjun. What's one thing that keeps slipping your mind? No rush.",
    tavo: "Hey, I'm Tavo! What's the one thing that keeps sneaking up on you too late? Tell me.",
  },
};
export const obHello = (ch: CharId, stuck?: string | null): string =>
  (OB_HELLO as Record<string, Record<CharId, string> | undefined>)[stuck || '']?.[ch] || LINES[ch].hello;
/** Where the thing they name is, going by what the opening line asked ("if what was asked about is already started,
 *  treat it as started"). Their own words and their answer to "Where are you with it?" come first. */
export const OB_HELLO_STAGE: Record<string, FirstWhere> = { Starting: 'new', 'Keeping going': 'middle', Finishing: 'almost', Remembering: 'new' };

/** O8 "Not quite" under the first card (F_O8 'notquite'): the user's bubble, the buddy's fixed line (no model call),
 *  and the three answers to "Where are you with it?" (sent as `where`); they can also say or type what's off (`fix`). */
export const OB_NOT_QUITE = 'Not quite.';
export const OB_NOT_QUITE_LINE: Record<CharId, string> = {
  sunny: 'Fair. Where are you with it? Or just tell me what I got wrong.',
  margo: "Okay. Where are you with it? Or tell me what's off.",
  arjun: 'Thanks for telling me. Where are you with it so far?',
  tavo: "Got it! Where are you with it? Or tell me what's off.",
};
export const OB_WHERE: { id: FirstWhere; label: string }[] = [
  { id: 'new', label: "Haven't started" },
  { id: 'middle', label: 'Partway through' },
  { id: 'almost', label: 'Almost done' },
];
/** The buddy's line before a first card that picks up work already under way (stage middle): never start over.
 *  Canned path (fake mode, the model's fallback, the app mock) and the model's style example. */
export const PICKUP_REPLY: Record<CharId, string> = {
  sunny: 'Okay, no starting over. We pick up right where you left it:',
  margo: "Okay. We don't start over. We pick up where you stopped:",
  arjun: "Then we won't start over. Let's pick up where you left it:",
  tavo: 'No restarting! We pick it up right where you left off:',
};
/** Stage almost: only the last bit is left (F_O8 'almost' replies). */
export const ALMOST_REPLY: Record<CharId, string> = {
  sunny: "Almost done? Then we're not touching the middle. Just the last bit:",
  margo: 'Nearly there. Then we only look at the last bit:',
  arjun: "Then it's close. Let's look at just the last part:",
  tavo: "Almost home! Then it's just the last stretch:",
};
/** Stage unknown (can't tell where they are, e.g. "Not quite" in words fake mode can't read): a step for any stage. */
export const NEXT_REPLY: Record<CharId, string> = {
  sunny: 'Okay, forget the whole thing. Just the next bit:',
  margo: 'Okay. Not the whole thing. Just the next step:',
  arjun: "Let's not look at the whole thing. Just the next small piece:",
  tavo: 'Alright! Not the whole thing. Just the next play:',
};

// ---------------------------------------------------------------- Crisis (M6), shared copy (not in character style)
export const CRISIS = {
  title: 'You deserve support from a person right now.',
  body: "If you're thinking about suicide or might hurt yourself, call or text 988 to reach the Suicide & Crisis Lifeline. It's free and open 24/7.",
  call: 'Call 988', text: 'Text 988', chat: 'Chat at 988lifeline.org', safe: "I'm safe. Keep talking",
  fine: '{n} is an AI and can\'t contact anyone for you. In immediate danger, call 911.',
};

// ---------------------------------------------------------------- Positioning (O1) and misc UI copy
export const POSITIONING = {
  label: 'INTERNAL PREVIEW', headline: 'A life buddy who gets you moving.',
  body: "Tell them what you keep putting off. They'll plan it with you, stay with you while you do it, and check in after.",
  rows: ['Plan your day in a quick chat', 'Do it together, not alone', 'Small wins, every day'],
  footer: "AI characters. Adults 18+. Internal build, please don't share.",
};
export const AI_NOTE = '{n} is an AI, not a therapist or a doctor.';
